import {customElement, property} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {UnLitElement} from "../../common/elements";
import {Dictionary} from "./dictionary";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {Nullable} from "../../bootstrap/common/types";
import {trackingTextlinkInteract} from "../../tracking/trackingAttributes";

export type BusinessCardData = {
    title?: string;
    infoBlock?: string;
    corporation?: string;
    address?: string;
    location?: string;
    phone?: string;
    fax?: string;
    mobile?: string;
    email?: string;
    link?: string;
    imageUrl?: string;
    imageAlt?: string;
};

@customElement("eop-business-card")
export class EopBusinessCard extends UnLitElement {

    @property({attribute: false})
    public data: BusinessCardData = {};

    private dictionary: Dictionary;

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public render(): TemplateResult {
        return html`
            ${this.renderImage()}
            <div class="business-card-text">
                <div class="business-card-title">${this.data.title}</div>
                ${this.renderInfo()}
                ${this.renderAddress()}
                ${this.renderContact()}
                ${this.renderEmail()}
                <div>${unsafeHTML(this.data.link ?? "")}</div>
            </div>
        `;
    }

    private renderImage(): Nullable<TemplateResult> {
        if (!this.data.imageUrl) {
            return null;
        }

        return html`
            <div class="business-card-image">
                <eop-responsive-image
                        .imageSrc=${this.data.imageUrl}
                        .imageAlt=${this.data.imageAlt ?? this.data.title ?? ""}
                ></eop-responsive-image>
            </div>
        `;
    }

    private renderInfo(): Nullable<TemplateResult> {
        if (!this.data.infoBlock) {
            return null;
        }

        return html`
            <div class="business-card-position">${unsafeHTML(this.data.infoBlock)}</div>`;
    }

    private renderAddress(): Nullable<TemplateResult> {
        const addressRows = [this.data.corporation, this.data.address, this.data.location].filter(Boolean);
        if (addressRows.isEmpty()) {
            return null;
        }

        return html`
            <div class="business-card-address">
                ${this.renderAddressRows(addressRows)}
            </div>
        `;
    }

    private renderAddressRows(rows: (string | undefined)[]): TemplateResult[] {
        return rows.map(r => html`
            <div class="contact-item">${r}</div>`);
    }

    private renderContact(): Nullable<TemplateResult> {
        const contactItems = [
            this.contactItem("CONTENT_LABEL_PHONE", this.data.phone, "tel-link", "tel"),
            this.contactItem("CONTENT_LABEL_FAX", this.data.fax, "fax-link", "tel"),
            this.contactItem("CONTENT_LABEL_MOBILE", this.data.mobile, "tel-link", "tel")
        ].filter(Boolean);
        if (contactItems.isEmpty()) {
            return null;
        }

        return html`
            <div class="labeled-values business-card-contact">${contactItems}</div>`;
    }

    private contactItem(labelKey: string, value: string | undefined, linkClass: string, protocol: string): Nullable<TemplateResult> {
        if (!value) {
            return null;
        }

        const href = `${protocol}:${value}`;
        return html`
            <div class="labeled-value-value contact-item">${this.dictionary.translate(labelKey)}</div>
            <div class="labeled-value-value contact-item">
                <a class="link ${linkClass}" href=${href} ${trackingTextlinkInteract(value, href)}>${value}</a>
            </div>
        `;
    }

    private renderEmail(): Nullable<TemplateResult> {
        if (!this.data.email) {
            return null;
        }

        const href = `mailto:${this.data.email}`;
        return html`
            <a class="link email-link business-card-email" href=${href} ${trackingTextlinkInteract(this.data.email, href)}>${this.data.email}</a>
        `;
    }
}
